/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Injectable, inject } from '@angular/core'
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, CanMatch, Route, RouterStateSnapshot, UrlSegment } from '@angular/router'
import { AuthConfig as Auth0AuthConfig, AuthorizationParams, AuthService } from '@auth0/auth0-angular'
import { Observable } from 'rxjs'
import { take, tap } from 'rxjs/operators'
import { ConfigService } from '../configuration/config.service'
import { WINDOW } from '@myprofile-ui/pidx-utils'

export type AuthConfig = Auth0AuthConfig & {
  logoutRedirectUri?: string
  skipTargetUriParam?: boolean
}

@Injectable({
  providedIn: 'root',
})
export class OAuthGuardService implements CanActivate, CanMatch, CanActivateChild {
  private configService = inject(ConfigService)
  private authService = inject(AuthService)
  private window = inject<Window>(WINDOW)

  canMatch(route: Route, segments: UrlSegment[]): Observable<boolean> {
    return this.authService.isAuthenticated$.pipe(take(1))
  }

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.redirectIfUnauthenticated(state)
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.redirectIfUnauthenticated(state)
  }

  protected redirectIfUnauthenticated(state: RouterStateSnapshot): Observable<boolean> {
    return this.authService.isAuthenticated$.pipe(
      tap((loggedIn) => {
        if (!loggedIn) {
          const country = this.configService.getUrlCountry()
          const language = this.configService.getUrlLocale().split('_').shift()

          // Get session_transfer_token from the target URL query params (passed from native app).
          const queryString = state.url.includes('?') ? state.url.substring(state.url.indexOf('?')) : ''
          const urlParams = new URLSearchParams(queryString)
          const sessionTransferToken = urlParams.get('session_transfer_token')

          const authorizationParams: AuthorizationParams = {
            // eslint-disable-next-line camelcase
            ui_locales: language,
            'ext-country': country,
            'ext-language': language,
          }
          if (sessionTransferToken) {
            authorizationParams['session_transfer_token'] = sessionTransferToken
          }
          authorizationParams['ext-target-uri'] = this.getFullURL()

          this.authService.loginWithRedirect({
            appState: { target: state.url },
            authorizationParams,
          })
        }
      }),
    )
  }

  private getFullURL(): string {
    const url = this.window.location.href
    const { hostname } = new URL(url)
    const pathname = this.window.location.pathname
    const indexOfQueryParamEncoding = pathname.indexOf('%3F')
    const indexOfQueryParam = pathname.indexOf('?')
    if (indexOfQueryParamEncoding === -1 && indexOfQueryParam === -1) {
      return `https://${hostname}${pathname}`
    } else if (indexOfQueryParamEncoding !== -1) {
      const newUrl = pathname.substring(0, indexOfQueryParamEncoding)
      return `https://${hostname}${newUrl}`
    } else {
      const newUrl = pathname.substring(0, indexOfQueryParam)
      return `https://${hostname}${newUrl}`
    }
  }
}
